import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { icon } from 'leaflet';

const values = [
  {
    icon: '🤝',
    title: 'Integrity',
    body: 'Transparent pricing, honest counsel, and no hidden agendas. We tell you exactly what something costs and exactly why we recommend it.'
  },
  {
    icon: '🎯',
    title: 'Commitment',
    body: 'We are dedicated to delivering dependable service at every stage of your journey. From your first enquiry to your safe return, we go the extra mile to ensure every travel experience exceeds expectations.'
  },
  {
    icon: '👔',
    title: 'Professionalism',
    body: 'Our experienced team combines industry expertise with attention to detail, providing timely, accurate, and courteous service. Every itinerary is managed with precision, ensuring a seamless and stress-free travel experience.'
  },
  {
    icon: '✨',
    title: 'Excellence',
    body: 'We hold ourselves to a standard that goes beyond satisfaction. Every interaction, every booking, every journey should feel exceptional.'
  },
  {
    icon: '🌍',
    title: 'Global Reach',
    body: 'With offices across West Africa and South Asia, and partners on every continent, we bring genuine local knowledge to every destination.'
  },
  {
    icon: '💼',
    title: 'Reliability',
    body: 'When plans change — and they do — we are the calm, capable presence that handles it. Our clients travel confidently because we never stop working.'
  }
];

const services = [
  {
    icon: '✈️',
    title: 'Flight & Visa Services',
    body: 'Best-fare ticketing across global airlines and comprehensive visa assistance — from document preparation to appointment scheduling.'
  },
  {
    icon: '💼',
    title: 'Customized Corporate Travel Solutions',
    body: 'Smart travel solutions with competitive corporate fares and exclusive business offers.'
  },
  {
    icon: '🏨',
    title: 'Premium Hotel Bookings',
    body: 'Carefully vetted accommodations at every price point, with direct relationships that unlock preferred rates and VIP treatment.'
  },
  {
    icon: '🧳',
    title: 'Customised Travel Packages',
    body: 'Tailor-made itineraries designed around individual preferences, timelines, and budgets — no two journeys are the same.'
  },
  {
    icon: '👑',
    title: 'Luxury Tours & Adventures',
    body: 'Exclusive access to premium properties, private transfers, and curated experiences that redefine what a holiday can be.'
  },
  {
    icon: '🌍',
    title: 'Other Services',
    body: 'From MICE and travel insurance to airport transfers and every other travel requirement, we deliver seamless, end-to-end travel solutions under one roof.'
  }
];

const offices = [
  { city: 'Dakar', country: 'Senegal', flag: '🇸🇳', type: 'Headquarters' },
  { city: 'Dakar Airport', country: 'Senegal', flag: '🛫', type: 'Airport Desk' },
  { city: 'Conakry', country: 'Guinea', flag: '🇬🇳', type: 'Regional Office' },
  { city: 'Abidjan', country: "Côte d'Ivoire", flag: '🇨🇮', type: 'Regional Office' },
  { city: 'Jaipur', country: 'India', flag: '🇮🇳', type: 'India Head Office' },
  { city: 'Libreville', country: 'Gabon', flag: '🇬🇦', type: 'Coming Soon' },
  { city: 'Dubai', country: 'UAE', flag: '🇦🇪', type: 'Coming Soon' },
  { city: 'Douala', country: 'Cameroon', flag: '🇨🇲', type: 'Coming Soon' }
];

const stats = [
  { number: '25+', label: 'Countries Served' },
  { number: '5', label: 'Office Locations' },
  { number: '10K+', label: 'Happy Travellers' },
  { number: '15+', label: 'Years of Excellence' }
];

function StatCard({ stat }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
      style={{
        textAlign: 'center', padding: '28px 20px',
        background: hovered ? 'var(--bj-clay)' : '#fff',
        borderRadius: 14,
        boxShadow: hovered
          ? '0 16px 40px rgba(63,114,175,0.25)'
          : '0 4px 20px rgba(17,45,78,0.08)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.3s ease',
        cursor: 'default'
      }}
    >
      <p style={{
        margin: '0 0 6px',
        fontFamily: 'var(--font-display)',
        fontSize: '2.4rem', fontWeight: 700,
        color: hovered ? '#fff' : 'var(--bj-clay)',
        lineHeight: 1, transition: 'color 0.3s'
      }}>
        {stat.number}
      </p>
      <p style={{
        margin: 0, fontSize: '0.82rem', fontWeight: 600,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        color: hovered ? 'rgba(255,255,255,0.8)' : 'var(--bj-espresso)',
        opacity: hovered ? 1 : 0.6,
        transition: 'all 0.3s'
      }}>
        {stat.label}
      </p>
    </div>
  );
}

function ValueCard({ value }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={function() { setHovered(true); }}
      onMouseLeave={function() { setHovered(false); }}
      style={{
        background: '#fff', borderRadius: 14,
        padding: '28px 24px',
        boxShadow: hovered
          ? '0 16px 40px rgba(17,45,78,0.12)'
          : '0 4px 20px rgba(17,45,78,0.06)',
        transform: hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.3s ease',
        borderTop: '3px solid ' + (hovered ? 'var(--bj-clay)' : 'var(--bj-sand)')
      }}
    >
      <div style={{
        width: 52, height: 52, borderRadius: 12,
        background: hovered ? 'var(--bj-clay)' : 'var(--bj-sand)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '1.4rem', marginBottom: 16,
        transition: 'background 0.3s'
      }}>
        {value.icon}
      </div>
      <h4 style={{ margin: '0 0 10px', color: 'var(--bj-espresso)' }}>{value.title}</h4>
      <p style={{ margin: 0, fontSize: '0.88rem', lineHeight: 1.7, opacity: 0.72 }}>{value.body}</p>
    </div>
  );
}

export default function About() {
  return (
    <Layout>

      {/* ── HERO BAND ── */}
      <div style={{
        background: 'linear-gradient(135deg, var(--bj-espresso) 0%, #1a3a5c 60%, #1e4f7a 100%)',
        padding: '88px 0 80px', position: 'relative', overflow: 'hidden'
      }}>
        <div style={{ position: 'absolute', top: -80, right: -80, width: 400, height: 400, borderRadius: '50%', background: 'rgba(63,114,175,0.1)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -60, left: -60, width: 280, height: 280, borderRadius: '50%', background: 'rgba(63,114,175,0.07)', pointerEvents: 'none' }} />

        <div className="container" style={{ position: 'relative' }}>
        <div className="about-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: 40, alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{
                  fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.18em',
                  textTransform: 'uppercase', color: 'var(--bj-sand)',
                  fontFamily: 'var(--font-body)'
                }}>
                  Sharda Group - Bluejay Travels
                </span>
                <div style={{ height: 1, width: 40, background: 'var(--bj-clay)' }} />
              </div>
              <h1 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'clamp(2rem, 5vw, 3.2rem)',
                color: '#fff', margin: '0 0 20px', fontWeight: 600, lineHeight: 1.15
              }}>
                Crafting journeys that<br />
                <span style={{ color: 'var(--bj-sand)' }}>matter</span>
              </h1>
              <p style={{
                color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem',
                lineHeight: 1.75, maxWidth: 520, margin: '0 0 32px'
              }}>
                Bluejay Travels is a distinguished brand within the Sharda Group —
                built on decades of industry expertise and a singular commitment to delivering
                travel experiences that genuinely exceed expectations.
              </p>
              <Link
                to="/leadership"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px',
                  background: 'rgba(255,255,255,0.12)',
                  backdropFilter: 'blur(8px)',
                  border: '1.5px solid rgba(255,255,255,0.25)',
                  borderRadius: 8, color: '#fff', fontWeight: 700,
                  fontSize: '0.9rem', textDecoration: 'none',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; }}
                onMouseLeave={function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.12)'; }}
              >
                👥 Meet our leadership →
              </Link>
            </div>

            {/* Stats grid */}
            <div className="grid-2" style={{ gap: 14 }}>
              {stats.map(function(s) {
                return <StatCard key={s.label} stat={s} />;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── FLIGHT PATH ── */}
      <div className="flight-path" />

      {/* ── WHO WE ARE ── */}
      <section className="section">
        <div className="container">
          <div className="two-col">
            <div>
              <span className="eyebrow">About Bluejay Travels</span>
              <h2 style={{ margin: '10px 0 20px' }}>A subsidiary built on a legacy of excellence</h2>
              <p style={{ lineHeight: 1.8, opacity: 0.8, marginBottom: 16 }}>
              Bluejay Travels is a specialised travel brand of the Sharda Group, built on decades of industry expertise, trusted partnerships, and operational excellence. With a customer-first approach, we deliver seamless travel experiences tailored to the unique needs of leisure travellers, corporate clients, and organisations across two continents.
              </p>
              <p style={{ lineHeight: 1.8, opacity: 0.8, marginBottom: 16 }}>
              Inspired by the bluejay—a bird known for its intelligence, confidence, and purposeful journeys—our brand reflects the way we approach every booking: with precision, reliability, and personalised care.
              </p>
              <p style={{ lineHeight: 1.8, opacity: 0.8, marginBottom: 16 }}>
              Our expertise spans customised holidays, business travel, flight bookings, hotels, visa assistance, travel insurance, airport transfers, cruises, car rentals, and holiday packages. We also provide comprehensive corporate travel management, including MICE, executive travel, group movements, negotiated corporate fares, and dedicated account support, helping businesses travel efficiently while optimising costs.
              </p>
              <p style={{ lineHeight: 1.8, opacity: 0.8, marginBottom: 16 }}>
              Backed by the legacy of the Sharda Group, Bluejay Travels is committed to making every journey simple, seamless, and memorable.
              </p>
            </div>
            <div>
              <div style={{
                background: 'var(--bj-espresso)', borderRadius: 20,
                padding: '36px 32px', position: 'relative', overflow: 'hidden'
              }}>
                <div style={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(63,114,175,0.15)', pointerEvents: 'none' }} />
                <span style={{ fontSize: '2rem', display: 'block', marginBottom: 16 }}><img
                  src="/sharda-logo.png"
                  alt=" Travel"
                  style={{ height: 48, width: 'auto', maxWidth: 160, objectFit: 'contain' }}
                /></span>
                <h3 style={{ color: '#fff', margin: '0 0 16px' }}>Part of Sharda Group</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.75, margin: '0 0 24px', fontSize: '0.92rem' }}>
                Sharda Group is a diversified conglomerate with nearly a decade of operations across South Asia and West Africa. Alongside its expertise in travel and tourism through Sharda Travel and Bluejay Travels, the group has also built a strong presence in import and export through GP Trading, creating a robust international business network. As its specialised travel brand, Bluejay Travels benefits from the group’s industry relationships, competitive rates, and operational infrastructure—delivering these advantages directly to every client.
                </p>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {['IATA Partner', 'Licensed Operator', 'Multi-Currency', 'B2B & B2C'].map(function(tag) {
                    return (
                      <span key={tag} style={{
                        background: 'rgba(63,114,175,0.3)',
                        color: 'var(--bj-sand)',
                        padding: '4px 12px', borderRadius: 20,
                        fontSize: '0.75rem', fontWeight: 700
                      }}>
                        {tag}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION / VISION ── */}
      <section className="section" style={{ background: 'var(--bj-cream-soft)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto 48px' }}>
            <span className="eyebrow">Our Foundation</span>
            <h2>What drives every decision we make</h2>
          </div>
          <div className="grid-3">
            {[
              {
                icon: '🎯',
                title: 'Our Mission',
                color: '#3F72AF',
                body: 'To make world-class travel accessible, transparent, and stress-free for every client—from the solo adventurer to the multinational corporation—regardless of budget or destination. We believe every journey should inspire confidence and create lasting memories. Through personalised service, global expertise, and an unwavering commitment to excellence, we turn travel dreams into seamless experiences without limits.'
              },
              {
                icon: '🔭',
                title: 'Our Vision',
                color: '#2d6a9f',
                body: 'To become the world’s most trusted name in personalised travel, connecting people across the world, anywhere to anywhere. We aspire to create extraordinary journeys across continents, not by being the biggest, but by delivering unforgettable experiences, seamless service, and lasting relationships that inspire travellers to explore the world without boundaries.'
              },
              {
                icon: '⭐',
                title: 'Our Promise',
                color: '#1a5c91',
                body: 'Our promise is to transform every trip into a memorable journey and every experience into a cherished story. From the first conversation to the final destination, we are committed to anticipating every detail, honouring every preference, and creating moments that travellers will treasure for a lifetime.'
              }
            ].map(function(item) {
              return (
                <div key={item.title} style={{
                  background: '#fff', borderRadius: 16, padding: '32px 28px',
                  boxShadow: '0 4px 20px rgba(17,45,78,0.07)',
                  borderBottom: '4px solid ' + item.color
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 14,
                    background: item.color + '18',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.5rem', marginBottom: 20
                  }}>
                    {item.icon}
                  </div>
                  <h3 style={{ margin: '0 0 14px', color: item.color }}>{item.title}</h3>
                  {item.title==='Our Vision'?<h4 style={{margin: '0 0 10px'}}>ACROSS THE WORLD - ANYWHERE TO ANYWHERE</h4>:""}
                  {item.title==='Our Promise'?<h4 style={{margin: '0 0 10px'}}>TO MAKE EVERY JOURNEY MEMORABLE AND CHERISHABLE</h4>:""}
                  {item.title==='Our Mission'?<h4 style={{margin: '0 0 10px'}}>ONE WORLD, LIMITLESS JOURNEYS</h4>:""}
                  <p style={{ margin: 0, lineHeight: 1.75, opacity: 0.78, fontSize: '0.92rem' }}>{item.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── KEY SERVICES ── */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto 48px' }}>
            <span className="eyebrow">What We Offer</span>
            <h2>A full spectrum of travel services</h2>
          </div>
          <div className="grid-3" style={{ gap: 22 }}>
            {services.map(function(s) {
              return (
                <div key={s.title} style={{
                  background: 'var(--bj-cream-soft)',
                  borderRadius: 14, padding: '24px 22px',
                  display: 'flex', gap: 16, alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: 10, flexShrink: 0,
                    background: 'var(--bj-sand)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem'
                  }}>
                    {s.icon}
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 8px', fontSize: '0.95rem' }}>{s.title}</h4>
                    <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.65, opacity: 0.72 }}>{s.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="section" style={{ background: 'var(--bj-cream-soft)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto 48px' }}>
            <span className="eyebrow">Our Values</span>
            <h2>The principles behind the practice</h2>
          </div>
          <div className="grid-3" style={{ gap: 22 }}>
            {values.map(function(v) {
              return <ValueCard key={v.title} value={v} />;
            })}
          </div>
        </div>
      </section>

      {/* ── INTERNATIONAL PRESENCE ── */}
      <section className="section">
        <div className="container">
          <div className="two-col">
            <div>
              <span className="eyebrow">International Presence</span>
              <h2 style={{ margin: '10px 0 20px' }}>Five cities. Two continents. One standard of excellence.</h2>
              <p style={{ lineHeight: 1.8, opacity: 0.8, marginBottom: 20 }}>
                Bluejay Travels operates a growing network of offices across West Africa and South Asia,
                each staffed by local specialists who bring genuine on-the-ground knowledge to every
                client interaction. Our presence in these markets is not symbolic — it is operational,
                relationships-driven, and constantly expanding.
              </p>
              <p style={{ lineHeight: 1.8, opacity: 0.8 }}>
                Our airline, hotel, and visa partners span every major destination we serve, allowing us
                to offer competitive rates and local expertise that no online booking platform can replicate.
                With upcoming offices in Libreville, Dubai and Douala the network continues to grow — guided by
                the same principle that launched it: be genuinely present where our clients need us most.
              </p>
              <Link
                to="/offices"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  marginTop: 24, padding: '11px 22px',
                  background: 'var(--bj-espresso)', color: '#fff',
                  borderRadius: 8, fontWeight: 700, fontSize: '0.9rem',
                  textDecoration: 'none'
                }}
              >
                View all office locations →
              </Link>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {offices.map(function(o) {
                var isUpcoming = o.type === 'Coming Soon';
                return (
                  <div key={o.city + o.country} style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '16px 20px',
                    background: isUpcoming ? 'transparent' : '#fff',
                    border: isUpcoming ? '2px dashed var(--bj-sand)' : '1px solid var(--bj-sand)',
                    borderRadius: 12,
                    opacity: isUpcoming ? 0.7 : 1
                  }}>
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>{o.flag}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem' }}>{o.city}</p>
                      <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.6 }}>{o.country}</p>
                    </div>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                      padding: '3px 10px', borderRadius: 20,
                      background: isUpcoming ? 'var(--bj-sand)' : 'var(--bj-clay)' + '18',
                      color: isUpcoming ? 'var(--bj-espresso)' : 'var(--bj-clay)'
                    }}>
                      {o.type}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM CTA ── */}
      <section style={{ background: 'var(--bj-espresso)', padding: '72px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: 'var(--bj-sand)',
            fontFamily: 'var(--font-body)', display: 'block', marginBottom: 16
          }}>
            The people behind the journey
          </span>
          <h2 style={{ color: '#fff', margin: '0 0 16px', fontSize: 'clamp(1.6rem, 4vw, 2.4rem)' }}>
            Meet the leadership that makes it all possible.
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', maxWidth: 520, margin: '0 auto 32px', lineHeight: 1.75 }}>
            Behind every seamless journey is a team of experienced, principled professionals
            who care deeply about getting the details right.
          </p>
          <Link
            to="/leadership"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '14px 28px',
              background: 'var(--bj-clay)',
              color: '#fff', borderRadius: 8,
              fontWeight: 700, fontSize: '0.95rem',
              textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(63,114,175,0.45)'
            }}
          >
            👥 Our Leadership Team →
          </Link>
        </div>
      </section>

    </Layout>
  );
}